/* eslint-disable max-len */
import React, { useEffect, useMemo, useState } from 'react'
import { connect as connectRedux, useSelector } from 'react-redux'
import ImmutablePropTypes from 'react-immutable-proptypes'
import PropTypes from 'prop-types'
import compose from 'recompose/compose'
import { Map, List } from 'immutable'
import { connect as connectPage, PAGE_SEO_TYPE_LOCATION } from '../../Page/connect'
import { connect as connectStaticText } from '../../StaticText/connect'
import { getAuthIsLoggedIn } from '../../../services/auth/index'
import { scrollToTop } from '../../../services/dom/index'
import { historyRedirect } from '../../../services/navigation/index'
import Button, { TYPES } from '../../Button/index'
import Sherpa, { TYPE_SMALL_BLUE } from '../../Sherpa/index'
import {
  URL_ACCOUNT,
  URL_ACCOUNT_CANCEL_OFFER,
  URL_ACCOUNT_CANCEL_CONFIRM,
} from '../../../services/url/constants'
import { useGetPlanDataBySku } from '../../../services/plans/hooks'
import { getPrimary } from '../../../services/languages/index'
import { getCurrencyIso } from '../../PlanCardGrid/utils'
import CountdownTimer, { FORMAT_DAYS_HRS_MINS_SECS } from '../../CountdownTimer/index'
import {
  shouldRenderCancelPriceIncreaseOffer,
  isFirstPromotionPeriod,
  userPauseAllowed,
  hasScheduledOrActivePause,
  shouldRenderCancelOffer,
} from '../../../services/user-account/index'
import { selectHasActiveGiftSubscription } from '../../../services/user-account/selectors'
import { getDateTime } from '../../../services/date-time/index'
import { TYPE_MY_ACCOUNT_CANCEL_MEMBERSHIP, TYPE_MY_ACCOUNT_CHANGE_PLAN_CONFIRM } from '../../../services/dialog/index'
import { MODAL_TYPES } from '../../Modal/index'
import { getBoundActions } from '../../../actions/index'
import { PLAN_CATEGORY_MONTHLY } from '../../../services/plans/index'
import {
  CANCEL_FLOW_PRICE_INCREASE_OFFER_SHOWN_GA4_EVENT,
  CANCEL_FLOW_PRICE_INCREASE_OFFER_SELECTED_GA4_EVENT,
  CANCEL_FLOW_PRICE_INCREASE_OFFER_REJECTED_GA4_EVENT,
} from '../../../services/event-tracking/index'
import SpecialOfferPlanCard from './SpecialOfferPlanCard'
import { getPlanOfferData } from './utils'

/**
 * Loading state for Account Cancel Special Offer
 * @returns {React.ReactNode} Loading state
 */
function AccountCancelSpecialOfferLoading () {
  return (
    <div className="account-cancel-special-offer__processing-overlay">
      <div className="account-cancel-special-offer__processing-content">
        <Sherpa type={TYPE_SMALL_BLUE} />
      </div>
    </div>
  )
}

/**
 * Account Cancel Special Offer
 * @param {Object} props - Component props
 * @returns {React.ReactNode} Account Cancel Special Offer
 */
function AccountCancelSpecialOffer (props) {
  const {
    auth,
    user,
    history,
    staticText,
    renderModal,
    userAccount,
    userLanguage,
    setEventDefault,
    setPlanChangeSelected,
    setUserAccountCancel,
    resetUserComp,
  } = props

  // If user is not logged in, return null
  if (!getAuthIsLoggedIn(auth)) {
    return null
  }

  const [hasVerifiedPermission, setHasVerifiedPermission] = useState(false)
  const [hasPermission, setHasPermission] = useState(false)
  const [shownEventSent, setShownEventSent] = useState(false)

  // SKUs come from state (set before navigating here). If missing, redirect to cancel page.
  const specialOfferSkusRaw = userAccount.getIn(['manageSubscription', 'data', 'specialOfferSkus'])
  const planSkus = useMemo(() => {
    if (!specialOfferSkusRaw) return null
    return List.isList(specialOfferSkusRaw) ? specialOfferSkusRaw.toJS() : specialOfferSkusRaw
  }, [specialOfferSkusRaw])

  // Determine the next URL to redirect to
  const nextUrl = useMemo(
    () => (shouldRenderCancelOffer(props) ? URL_ACCOUNT_CANCEL_OFFER : URL_ACCOUNT_CANCEL_CONFIRM),
    [props],
  )

  // Second Window: Price Increase Offer
  const planCategory = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'planCategory']) || ''
  const isMonthly = planCategory === PLAN_CATEGORY_MONTHLY
  const isInFirstPromoPeriod = isFirstPromotionPeriod(planCategory)

  const isProcessing = userAccount.get('billingSubscriptionsProcessing', false)
  const userPrimaryLanguage = getPrimary(userLanguage)
  const authJwt = auth.get('jwt')

  // Calculate countdown duration until final price increase  date
  const finalPriceIncreaseDate = isMonthly ? getDateTime('2026-03-31T23:00:00Z') : getDateTime('2026-03-02T23:00:00Z')
  const countdownDurationMs = useMemo(() => {
    return Math.max(0, finalPriceIncreaseDate - Date.now())
  }, [])

  useEffect(() => {
    // If no plan SKUs are available, redirect to the next URL
    if (!planSkus || (Array.isArray(planSkus) && planSkus.length === 0)) {
      historyRedirect({
        history,
        url: nextUrl,
        auth,
        language: userLanguage,
      })
    }
  }, [planSkus])

  // Get plans from GraphQL by SKU - only fetch if planSkus is not empty
  const plansData = useGetPlanDataBySku({
    userLanguage: userPrimaryLanguage,
    authJwt,
    planSkus,
    forceRefresh: true,
  })

  // Scroll to top of the page on mount
  useEffect(() => {
    scrollToTop()
  }, [])

  // Get plans list from GraphQL and reorder to match planSkus order
  const plansList = useMemo(() => {
    if (!planSkus || !Array.isArray(planSkus) || planSkus.length === 0) return List()
    const plans = plansData.get('plans', List())
    return plans
      .filter((plan) => planSkus.includes(plan.get('sku')))
      .sort((a, b) => planSkus.indexOf(a.get('sku')) - planSkus.indexOf(b.get('sku')))
  }, [plansData, planSkus])

  // Calculate currency once for both plan transformation and click handler
  const currencyIso = useMemo(() => getCurrencyIso({
    userAccount,
    plansList,
    selectedPlan: Map(),
  }), [userAccount, plansList])

  // Transform plans data for PlanCard
  const transformedPlans = useMemo(() => {
    if (plansList.size === 0) return List()
    return getPlanOfferData({
      plans: plansList,
      staticText,
      userLanguage: userPrimaryLanguage,
      isInFirstPromoPeriod,
    })
  }, [plansList])

  // Pause options
  const currentPlanIsGift = useSelector(selectHasActiveGiftSubscription)
  const userCanPause = userPauseAllowed(props) && !hasScheduledOrActivePause(userAccount)
  const showPauseModal = !currentPlanIsGift && userCanPause

  // Verify user permission to see this page
  useEffect(() => {
    // Wait until user and userAccount are available
    const userData = user.get('data')
    const userAccountDetails = userAccount.get('details')
    const isUserDataLoaded = userData && Map.isMap(userData)
    const isUserAccountLoaded = userAccountDetails && Map.isMap(userAccountDetails)

    if (!isUserDataLoaded || !isUserAccountLoaded) {
      return
    }

    // Check if user has already been verified
    if (hasVerifiedPermission) {
      return
    }

    // Verify permission using shouldRenderCancelPriceIncreaseOffer
    const canViewOffer = shouldRenderCancelPriceIncreaseOffer({ user, userAccount })

    setHasVerifiedPermission(true)
    setHasPermission(canViewOffer)

    // Redirect to MyAccount if user doesn't have permission
    if (!canViewOffer) {
      historyRedirect({
        history,
        url: URL_ACCOUNT,
        auth,
        language: userLanguage,
      })
    }
  }, [hasVerifiedPermission])

  // Track GA4 event when offer is shown
  useEffect(() => {
    if (hasPermission && transformedPlans.size > 0 && !shownEventSent) {
      setEventDefault(CANCEL_FLOW_PRICE_INCREASE_OFFER_SHOWN_GA4_EVENT)
      setShownEventSent(true)
    }
  }, [hasPermission, transformedPlans.size])

  const openPauseModal = (url) => {
    resetUserComp()
    renderModal(TYPE_MY_ACCOUNT_CANCEL_MEMBERSHIP, {
      hideOverflow: true,
      hideDismiss: false,
      url,
      type: MODAL_TYPES.MOBILE_BOTTOM_ALIGNED,
    })
  }

  const onClickCancelAnyway = () => {
    setEventDefault(CANCEL_FLOW_PRICE_INCREASE_OFFER_REJECTED_GA4_EVENT)
    const freeOfferEligible = nextUrl === URL_ACCOUNT_CANCEL_OFFER

    // Defer cancel to the pause modal (same as AccountCancelPage) to avoid a duplicate PUT.
    if (!freeOfferEligible && !showPauseModal) {
      setUserAccountCancel()
    }
    if (showPauseModal) {
      openPauseModal(nextUrl)
    } else {
      historyRedirect({
        history,
        url: nextUrl,
        auth,
        language: userLanguage,
      })
    }
  }

  // Handle Accept Offer button click
  const handlePlanClick = (transformedPlan) => {
    // Get the original plan from plansList using the SKU
    const planSku = transformedPlan.get('sku')
    const originalPlan = plansList.find((plan) => plan.get('sku') === planSku) || Map()

    if (originalPlan.size === 0) {
      return
    }

    // scroll to the top of the page
    scrollToTop()

    // Track event when user selects a plan
    setEventDefault(CANCEL_FLOW_PRICE_INCREASE_OFFER_SELECTED_GA4_EVENT)

    // Open change plan confirmation modal
    setPlanChangeSelected(originalPlan)
    renderModal(TYPE_MY_ACCOUNT_CHANGE_PLAN_CONFIRM)
  }

  // Show loading state if plans are still loading or if transformedPlans is empty
  const isLoading = plansData.get('loading', false) || transformedPlans.size === 0
  if (!hasVerifiedPermission || !hasPermission || isLoading) {
    return <AccountCancelSpecialOfferLoading />
  }

  return (
    <div className="account-cancel-special-offer">
      { isProcessing && <AccountCancelSpecialOfferLoading /> }
      <div className="account-cancel-special-offer__background"></div>
      <div className={`account-cancel-special-offer__content${isProcessing ? ' account-cancel-special-offer__content--processing' : ''}`}>
        {isInFirstPromoPeriod ? (
          <>
            <div className="account-cancel-special-offer__title">
              {staticText.getIn(['data', 'title'])}
            </div>
            <div className="account-cancel-special-offer__description">
              <p>{staticText.getIn(['data', 'description1'])}</p>
              <p className="account-cancel-special-offer__hide-mobile">{staticText.getIn(['data', 'description2'])}</p>
              <p>
                <CountdownTimer
                  durationMs={countdownDurationMs}
                  format={FORMAT_DAYS_HRS_MINS_SECS}
                  staticText={staticText.get('data', Map())}
                  timerClass={['account-cancel-special-offer__countdown']}
                />
                {staticText.getIn(['data', 'timeLeft'], '').replace(/\$\{countdown\}/g, '')}
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="account-cancel-special-offer__title">{staticText.getIn(['data', 'changePlan'])}</div>
            <div className="account-cancel-special-offer__description">
              <p>{staticText.getIn(['data', 'description4'])}</p>
              <p>{staticText.getIn(['data', 'description5'])}</p>
            </div>
          </>
        )}
        <div className="account-cancel-special-offer__plans-selection">
          <div className="account-cancel-special-offer__plans">
            {transformedPlans.map((plan) => {
              const sku = plan.get('sku')
              return (
                <SpecialOfferPlanCard
                  key={sku}
                  plan={plan}
                  onPlanClick={handlePlanClick}
                  isProcessing={isProcessing}
                  staticText={staticText}
                />
              )
            })}
          </div>
        </div>
        <div className="account-cancel-special-offer__disclaimer">
          {staticText.getIn(['data', 'disclaimer']).replace(/\$\{currencyIso\}/g, currencyIso)}
        </div>
        <div className="account-cancel-special-offer__buttons">
          <Button
            type={TYPES.GHOST}
            onClick={onClickCancelAnyway}
            className="account-cancel-special-offer__cancel-button"
          >
            {staticText.getIn(['data', 'cancelAnyway'])}
          </Button>
        </div>
      </div>
    </div>
  )
}

AccountCancelSpecialOffer.propTypes = {
  auth: ImmutablePropTypes.map.isRequired,
  history: PropTypes.object.isRequired,
  userLanguage: ImmutablePropTypes.list.isRequired,
}

export default compose(
  connectPage({ seoType: PAGE_SEO_TYPE_LOCATION }),
  connectStaticText({ storeKey: 'accountCancelSpecialOffer' }),
  connectRedux(
    (state) => ({
      auth: state.auth,
      user: state.user,
      userLanguage: state.user.getIn(['data', 'language'], []),
      userAccount: state.userAccount,
      plans: state.plans,
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setPlanChangeSelected: actions.plans.setPlanChangeSelected,
        setEventDefault: actions.eventTracking.setEventDefault,
        renderModal: actions.dialog.renderModal,
        setUserAccountCancel: actions.userAccount.setUserAccountCancel,
        resetUserAccountManageSubscriptionData: actions.userAccount.resetUserAccountManageSubscriptionData,
        resetUserComp: actions.subscription.resetUserComp,
      }
    },
  ),
)(AccountCancelSpecialOffer)
